/**
 * App chrome theming — meta theme-color + native status bar.
 *
 * Two independent inputs decide the chrome colour:
 *   1. The user's resolved app theme ('light' | 'dark'), pushed by ThemeContext.
 *   2. An "immersive" override count, pushed by intentionally-dark full-bleed
 *      screens (Finale, share cards, cinematic loaders). While any of those are
 *      mounted the chrome is forced DARK regardless of the app theme, so a
 *      light-mode user never sees light chrome bleeding onto a dark screen.
 *
 * Everything here is null-safe and no-ops outside the browser.
 */

export type ResolvedTheme = 'light' | 'dark';

/** Must match --hos-bg / --scrim-base in index.css. */
export const CHROME_COLOR: Record<ResolvedTheme, string> = {
  light: '#F4F5F7',
  dark: '#070809',
};

let appTheme: ResolvedTheme = 'dark';
let forcedDarkCount = 0;

function effectiveTheme(): ResolvedTheme {
  return forcedDarkCount > 0 ? 'dark' : appTheme;
}

function applyMetaThemeColor(theme: ResolvedTheme) {
  if (typeof document === 'undefined') return;
  try {
    // Drop any media-scoped variants left over from the static head — they key
    // off prefers-color-scheme and would override the app's own choice.
    const all = document.querySelectorAll('meta[name="theme-color"]');
    let primary: HTMLMetaElement | null = null;
    all.forEach((node) => {
      const meta = node as HTMLMetaElement;
      if (meta.hasAttribute('media')) {
        meta.remove();
        return;
      }
      if (!primary) primary = meta;
    });
    if (!primary) {
      primary = document.createElement('meta');
      primary.name = 'theme-color';
      document.head.appendChild(primary);
    }
    (primary as HTMLMetaElement).setAttribute('content', CHROME_COLOR[theme]);
  } catch {
    /* no-op */
  }
}

function applyNativeStatusBar(theme: ResolvedTheme) {
  // @capacitor/status-bar is NOT a dependency of this project. Rather than add
  // one in this pass, probe the Capacitor bridge at runtime: if the plugin ever
  // gets installed the call starts working with no further changes here.
  try {
    const cap = (globalThis as unknown as {
      Capacitor?: {
        isNativePlatform?: () => boolean;
        Plugins?: Record<string, { setStyle?: (o: { style: string }) => Promise<void> }>;
      };
    }).Capacitor;
    if (!cap?.isNativePlatform?.()) return;
    const statusBar = cap.Plugins?.StatusBar;
    if (!statusBar?.setStyle) return;
    // Capacitor's Style.Dark means "dark content backdrop" → light text.
    void Promise.resolve(statusBar.setStyle({ style: theme === 'dark' ? 'DARK' : 'LIGHT' })).catch(
      () => undefined,
    );
  } catch {
    /* no-op */
  }
}

function syncChrome() {
  const theme = effectiveTheme();
  applyMetaThemeColor(theme);
  applyNativeStatusBar(theme);
}

/** Called by ThemeContext whenever the resolved app theme changes. */
export function setAppChromeTheme(theme: ResolvedTheme) {
  appTheme = theme === 'light' ? 'light' : 'dark';
  syncChrome();
}

/** Called on mount by an intentionally-dark full-bleed screen. */
export function pushForcedDarkChrome() {
  forcedDarkCount += 1;
  syncChrome();
}

/** Called on unmount by an intentionally-dark full-bleed screen. */
export function popForcedDarkChrome() {
  forcedDarkCount = Math.max(0, forcedDarkCount - 1);
  syncChrome();
}

export function isChromeForcedDark() {
  return forcedDarkCount > 0;
}
